import { Injectable, computed, signal } from '@angular/core';

import {
  OAuthService,
  UserIntegrationsResponse,
} from 'src/app/components/content-oauth/services/oauth.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import {
  CANVA_PROVIDER,
  CanvaService,
} from 'src/app/template-editor/template-components/services/canva.service';
import { GoogleService } from 'src/app/template-editor/template-components/services/google.service';
import { MicrosoftService } from 'src/app/template-editor/template-components/services/microsoft.service';
import { SocialMediaService } from 'src/app/template-editor/template-components/services/social-media.service';

import {
  FACEBOOK_PAGES_PROVIDER,
  INSTAGRAM_PROVIDER,
  RECONNECT_SCOPES,
  USER_PROVIDER_META,
} from '../models/integrations-user.catalog';
import {
  ConnectedIntegrationRow,
  UserIntegration,
  UserIntegrationRef,
} from '../models/integrations.types';

export type {
  UserIntegration as UserIntegration,
  UserIntegrationRef,
  UserIntegrationStatus,
} from '../models/integrations.types';

const DISCONNECT_MESSAGE =
  'This will remove the integration and break links across all connected presentations. To restore your data, you will have to open each presentation individually and re-enter your credentials.';

interface FacebookPage {
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class IntegrationsUserService {
  private readonly _integrations = signal<UserIntegration[]>([]);

  constructor(
    private oAuthService: OAuthService,
    private googleService: GoogleService,
    private microsoftService: MicrosoftService,
    private canvaService: CanvaService,
    private socialMediaService: SocialMediaService,
    private modalService: ModalService,
    private snackbarService: SnackbarService,
  ) {}

  /** Connected-row shape derived from the loaded user integrations. */
  readonly integrations = computed((): ConnectedIntegrationRow[] =>
    this._integrations().map((row) => ({
      id: row.providerId,
      name: row.providerName,
      username: row.username || undefined,
      canReconnect: this.canReconnect(row),
      status: row.status,
      account: row.account,
      logo: row.logo,
    })),
  );

  async load(): Promise<void> {
    try {
      await this.list();
    } catch (error) {
      console.error('Failed to load user integrations:', error);
    }
  }

  async list(): Promise<UserIntegration[]> {
    const response = await this.oAuthService.getUserIntegrations();
    const rows = this.normalize(response);
    this._integrations.set(rows);
    return rows;
  }

  find(id: string, account?: string): UserIntegration | undefined {
    return this._integrations().find(
      (row) => row.providerId === id && row.account === account,
    );
  }

  canReconnect(row: UserIntegration): boolean {
    return (
      (row.status === 'expired' || row.status === 'unknown') &&
      !!this.resolveScope(row)
    );
  }

  /**
   * Reconnects a listed integration and reloads the list.
   */
  async reconnectIntegration(integration: UserIntegrationRef): Promise<void> {
    const row = this.find(integration.id, integration.account);
    if (!row) {
      return;
    }

    try {
      await this.reconnect(row);
      await this.load();
      this.showSuccessSnackbar('Reconnected', row.providerName);
    } catch (error) {
      console.error('Failed to reconnect user integration:', error);
      this.showErrorSnackbar(
        `Failed to reconnect ${row.providerName} integration.`,
      );
    }
  }

  /**
   * Shows the disconnect confirm dialog.
   * Returns the matched row when confirmed, otherwise null.
   */
  async confirmDisconnectIntegration(
    integration: UserIntegrationRef,
  ): Promise<UserIntegration | null> {
    const row = this.find(integration.id, integration.account);
    if (!row) {
      return null;
    }

    try {
      await this.modalService.confirmDanger(
        'Remove integration?',
        DISCONNECT_MESSAGE,
        'Remove',
        'Cancel',
      );
      return row;
    } catch {
      return null;
    }
  }

  /**
   * Revokes and reloads after the user confirmed Remove.
   */
  async disconnectIntegration(row: UserIntegration): Promise<void> {
    try {
      await this.disconnect(row);
      await this.load();
      this.showSuccessSnackbar('Disconnected', row.providerName);
    } catch (error: unknown) {
      console.error('Failed to remove user integration:', error);
      this.showErrorSnackbar(
        `Failed to remove ${row.providerName} integration.`,
      );
    }
  }

  reconnect(row: UserIntegration): Promise<unknown> {
    const scope = this.resolveScope(row);

    if (!scope) {
      return Promise.reject(
        new Error(`No reconnect scope for provider: ${row.providerId}`),
      );
    }

    const authenticated = this.oAuthService.authenticate(row.providerId, scope);

    switch (row.providerId) {
      case FACEBOOK_PAGES_PROVIDER:
        return authenticated.then(() => this.restorePageTokens(row.account));
      case INSTAGRAM_PROVIDER:
        return authenticated.then((result) =>
          this.replaceReconnectedAccount(row, result),
        );
      default:
        return authenticated;
    }
  }

  disconnect(row: UserIntegration): Promise<unknown> {
    switch (row.providerId) {
      case 'google':
        return this.googleService.revoke(row.account, 'google');
      case 'facebook-consumer':
      case 'facebook-business':
      case 'instagram-business':
        return this.googleService.revoke(row.account, row.providerId);
      case 'ms-graph':
      case 'ms-pbi':
        // OTP revoke of either Microsoft provider clears both (ms-graph + ms-pbi).
        return this.microsoftService.revoke(row.providerId);
      case CANVA_PROVIDER:
        return this.canvaService.revoke(CANVA_PROVIDER);
      default:
        return Promise.reject(
          new Error(`Unsupported user integration provider: ${row.providerId}`),
        );
    }
  }

  // Instagram credentials are keyed per Instagram account, so a different
  // account reconnecting adds a row instead of replacing — revoke the stale one.
  private async replaceReconnectedAccount(
    row: UserIntegration,
    result: { account?: string },
  ): Promise<void> {
    if (!result?.account || result.account === row.account) {
      return;
    }

    try {
      await this.disconnect(row);
    } catch (error) {
      console.error(
        `Failed to remove replaced Instagram account ${row.account}:`,
        error,
      );
    }
  }

  private async restorePageTokens(account: string): Promise<void> {
    const pages: FacebookPage[] =
      await this.socialMediaService.getPages(account);

    const results = await Promise.allSettled(
      pages.map((page) => this.socialMediaService.selectPage(account, page.id)),
    );

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(
          `Failed to restore Facebook page token for page ${pages[index].id}:`,
          result.reason,
        );
      }
    });
  }

  private resolveScope(row: UserIntegration): string {
    return RECONNECT_SCOPES[row.providerId] || '';
  }

  private showSuccessSnackbar(action: string, integrationName: string): void {
    this.snackbarService.show({
      role: 'success',
      text: `${action} ${integrationName} integration.`,
      duration: 'short',
    });
  }

  private showErrorSnackbar(text: string): void {
    this.snackbarService.show({
      role: 'error',
      text,
      duration: 'long',
    });
  }

  private normalize(response: UserIntegrationsResponse): UserIntegration[] {
    // Keyed by group (merged providers share a groupId) so ms-graph + ms-pbi
    // collapse into one Microsoft row.
    const rows = new Map<string, UserIntegration>();

    for (const [providerId, accounts] of Object.entries(response)) {
      const meta = USER_PROVIDER_META[providerId];
      for (const account of accounts) {
        const groupKey = meta?.groupId ?? `${providerId}:${account.account}`;
        const existing = rows.get(groupKey);
        if (existing) {
          if (providerId === 'ms-graph') {
            existing.providerId = providerId;
            existing.username = account.username;
            existing.account = account.account;
            existing.status = account.status ?? 'connected';
          }
          continue;
        }

        rows.set(groupKey, {
          providerId,
          providerName: meta?.name ?? providerId,
          username: account.username,
          account: account.account,
          status: account.status ?? 'connected',
          logo: meta?.logo ?? '',
        });
      }
    }

    return [...rows.values()]
      .sort((a, b) => {
        const byProvider = a.providerName.localeCompare(b.providerName);
        return byProvider || a.username.localeCompare(b.username);
      })
      .map((row) =>
        USER_PROVIDER_META[row.providerId]?.hideUsername
          ? { ...row, username: '' }
          : row,
      );
  }
}
